import { OrganizationMembership, useGetUserMemberships } from "@/api";
import { TypographySM } from "@/components/typography/SM";
import { TypographyXS } from "@/components/typography/XS";
import Card from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PendingInvitationCard } from "@/features/organization-invitations";
import { Link } from "@tanstack/react-router";
import { isBefore, isSameSecond } from "date-fns";
import { PlusSquare } from "lucide-react";
import { useLocalStorage } from "usehooks-ts";
import { LoadingOrganizations } from "./LoadingOrganizations";
import { NoOrganizations } from "./NoOrganizations";
import { SelectableOrganizations } from "./SelectableOrganizations";

const sortByTime = (a: OrganizationMembership, b: OrganizationMembership) => {
  if (isBefore(a.organization.createdAt, b.organization.createdAt)) return 1;
  if (isSameSecond(a.organization.createdAt, b.organization.createdAt))
    return 0;
  return -1;
};

export const SelectOrganization = () => {
  const { data: memberships, loading } = useGetUserMemberships();
  const sortedOrganizationMemberships = memberships.toSorted(sortByTime);

  const [, setLatestOrganizationSlug] = useLocalStorage<string | null>(
    "latestOrganizationSlug",
    null
  );

  if (loading) return <LoadingOrganizations />;
  if (
    !sortedOrganizationMemberships ||
    sortedOrganizationMemberships.length === 0
  )
    return <NoOrganizations />;
  return (
    <div className="flex flex-col justify-center items-center w-96 mx-auto">
      <PendingInvitationCard className="mb-2" />
      <Card data-testid="selectOrganizationCard" className="w-full">
        <Card.Header className="gap-1">
          <Card.Title className="text-2xl text-center">Organization</Card.Title>
          <Card.Description className="text-center">
            Select the organization you want to use.
          </Card.Description>
        </Card.Header>
        <Card.Content>
          <ScrollArea className="h-72">
            <SelectableOrganizations
              setLatestOrganizationSlug={setLatestOrganizationSlug}
              organizationMemberships={sortedOrganizationMemberships}
            />
          </ScrollArea>

          <Link
            to="/organization-create"
            search={{ redirect: undefined }}
            data-testid="createNewOrganizationRow"
            className="flex items-center text-sm justify-between w-full border-t font-medium dark:text-brand-primary p-2 border-b transition-colors hover:bg-muted/50 bg-brand-gray-50 dark:bg-brand-gray-900"
          >
            <TypographySM>Create organization</TypographySM>
            <PlusSquare size={16} />
          </Link>
          <TypographyXS
            data-testid="userMemberAmount"
            className="text-brand-gray-400 mt-4 text-center"
          >
            You are a member of {sortedOrganizationMemberships.length}{" "}
            organization
            {sortedOrganizationMemberships.length !== 1 && "s"}.
          </TypographyXS>
        </Card.Content>
      </Card>
    </div>
  );
};
